import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import momentPropTypes from 'react-moment-proptypes';
import { forbidExtraProps, nonNegativeInteger } from 'airbnb-prop-types';
import { isInclusivelyAfterDay, toMomentObject, toLocalizedDateString } from 'react-dates';

import openDirectionShape from './shapes/OpenDirectionShape';
import IconPositionShape from './shapes/IconPositionShape';

import DateValidator from './utils/DateValidator';
import getPhrasePropTypes from './utils/getPhrasePropTypes';
import isBeforeDay from './utils/isBeforeDay';
import { DateRangePickerInputPhrases } from './utils/defaultPhrases';

import DateRangePickerInput from './DateRangePickerInput';

import { START_DATE, END_DATE, ICON_BEFORE_POSITION, OPEN_DOWN } from './constants';

const propTypes = forbidExtraProps({
    startDate: momentPropTypes.momentObj,
    startDateId: PropTypes.string,
    startDatePlaceholderText: PropTypes.string,
    isStartDateFocused: PropTypes.bool,

    endDate: momentPropTypes.momentObj,
    endDateId: PropTypes.string,
    endDatePlaceholderText: PropTypes.string,
    isEndDateFocused: PropTypes.bool,

    lowerLimit: momentPropTypes.momentObj,
    upperLimit: momentPropTypes.momentObj,

    showClearDates: PropTypes.bool,
    showCaret: PropTypes.bool,
    showDefaultInputIcon: PropTypes.bool,
    inputIconPosition: IconPositionShape,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    readOnly: PropTypes.bool,
    openDirection: openDirectionShape,

    reopenPickerOnClearDates: PropTypes.bool,
    withFullScreenPortal: PropTypes.bool,
    minimumNights: nonNegativeInteger,
    isOutsideRange: PropTypes.func,
    isDayBlocked: PropTypes.func,
    displayFormat: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

    onFocusChange: PropTypes.func,
    onClose: PropTypes.func,
    onDatesChange: PropTypes.func,
    onArrowDown: PropTypes.func,
    onQuestionMark: PropTypes.func,

    customInputIcon: PropTypes.node,
    customArrowIcon: PropTypes.node,
    customCloseIcon: PropTypes.node,

    // accessibility
    isFocused: PropTypes.bool,

    // i18n
    phrases: PropTypes.shape(getPhrasePropTypes(DateRangePickerInputPhrases)),

    isRTL: PropTypes.bool
});

const defaultProps = {
    startDate: null,
    startDateId: START_DATE,
    startDatePlaceholderText: 'Check in',
    isStartDateFocused: false,

    endDate: null,
    endDateId: END_DATE,
    endDatePlaceholderText: 'Check out',
    isEndDateFocused: false,

    lowerLimit: null,
    upperLimit: null,

    showClearDates: false,
    showCaret: false,
    showDefaultInputIcon: false,
    inputIconPosition: ICON_BEFORE_POSITION,
    disabled: false,
    required: false,
    readOnly: false,
    openDirection: OPEN_DOWN,

    reopenPickerOnClearDates: false,
    withFullScreenPortal: false,
    minimumNights: 1,
    isOutsideRange: (day) => !isInclusivelyAfterDay(day, moment()),
    isDayBlocked: () => false,
    displayFormat: () => moment.localeData().longDateFormat('L'),

    onFocusChange() {},
    onClose() {},
    onDatesChange() {},
    onArrowDown() {},
    onQuestionMark() {},

    customInputIcon: null,
    customArrowIcon: null,
    customCloseIcon: null,

    // accessibility
    isFocused: false,

    // i18n
    phrases: DateRangePickerInputPhrases,

    isRTL: false
};

export default class DateRangePickerInputController extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            startDateError: '',
            endDateError: '',
            startDisplayErrorDate: '',
            endDisplayErrorDate: ''
        };

        this.onClearFocusStart = this.onClearFocusStart.bind(this);
        this.onClearFocusEnd = this.onClearFocusEnd.bind(this);
        this.onStartDateFocus = this.onStartDateFocus.bind(this);
        this.onEndDateFocus = this.onEndDateFocus.bind(this);
        this.clearDates = this.clearDates.bind(this);
    }

    componentWillReceiveProps = (nextProps) => {
        if (nextProps.startDate && nextProps.startDate !== this.props.startDate) {
            this.setState({
                startDateError: '',
                startDisplayErrorDate: ''
            });
        }

        if (nextProps.endDate && nextProps.endDate !== this.props.endDate) {
            this.setState({
                endDateError: '',
                endDisplayErrorDate: ''
            });
        }
    };

    onClearFocusEnd(value) {
        // onClearFocus is called by endDate's tab keyDown event, and is required
        // to make the portal close and fire onClose. However, it often fires before
        // onEndDateBlur, causing the onEndDateBlur not to fire at all. So we're
        // calling it here. Adding a setTimeout of 0 to this function does allow
        // onBlur to be called, however it causes a new problem where the focus
        // ring moves to the portal right as the portal is being closed, causing
        // the focus to be lost and creating up a keyboard trap.
        setTimeout(() => {
            this.onEndDateBlur(value);
        }, 0);

        const { onFocusChange, onClose, startDate, endDate } = this.props;

        onFocusChange(null);
        onClose({ startDate, endDate });
    }

    onClearFocusStart(value) {
        // onClearFocus is called by startDate's shift-tab keyDown event, and
        // is required to make the portal close and fire onClose. See logic in
        // comment above
        this.onStartDateBlur(value);

        const { onFocusChange, onClose, startDate, endDate } = this.props;

        onFocusChange(null);
        onClose({ startDate, endDate });
    }

    onEndDateBlur = (value) => {
        // Validate endDate when the user moves away

        const { startDate, endDate, onDatesChange, isOutsideRange, isDayBlocked, minimumNights, lowerLimit, upperLimit } = this.props;

        if (!endDate && !value && !this.state.endDateError)  {
            // Don't fire onChange is there's no change
            return
        }

        // If empty, reset validation, clear date
        if (!value) {
            this.setState({
                endDateError: ''
            });
            onDatesChange({
                startDate,
                endDate: null
            });
            return;
        }

        let calcedLowerLimit;

        if (startDate) {
            calcedLowerLimit = startDate.clone().add(minimumNights, 'days');
        }
        else if (lowerLimit) {
            calcedLowerLimit = lowerLimit.add(minimumNights, 'days');
        }
        else {
            calcedLowerLimit = moment().add(minimumNights, 'days');
        }

        const parsed = DateValidator({
            input: value,
            lowerLimit: calcedLowerLimit,
            upperLimit: upperLimit || moment().add(10, 'years')
        });

        if (parsed.isValidMoment && moment(endDate).isSame(parsed.moment, 'day')) {
            // Day hasn't changed so ignore - likely user using the calendar picker
            return;
        }

        if (!parsed.isValid) {
            this.setState({
                endDateError: parsed.errorMessage
            });

            onDatesChange({
                startDate,
                endDate: null
            });

            if (parsed.isValidMoment) {
                this.setState({
                    endDisplayErrorDate: this.getDateString(parsed.moment)
                });
            }
        }
        else if (isOutsideRange(parsed.moment) || isDayBlocked(parsed.moment)) {
            this.setState({
                endDateError: 'is not available.'
            });

            onDatesChange({
                startDate,
                endDate: null
            });

            if (parsed.isValidMoment) {
                this.setState({
                    endDisplayErrorDate: this.getDateString(parsed.moment)
                });
            }
        }
        else {
            onDatesChange({
                startDate,
                endDate: parsed.moment.startOf('day')
            });

            this.setState({
                endDateError: ''
            });
        }
    };

    onEndDateFocus() {
        const { startDate, onFocusChange, withFullScreenPortal, disabled } = this.props;

        if (!startDate && withFullScreenPortal && !disabled) {
            // When the datepicker is full screen, we never want to focus the
            // end date first because there's no indication that that is the
            // case once the datepicker is open and it might confuse the user
            onFocusChange(START_DATE);
        }
        else if (!disabled) {
            onFocusChange(END_DATE);
        }
    }

    onStartDateBlur = (value) => {
        const { startDate, endDate, onDatesChange, isOutsideRange, isDayBlocked, lowerLimit, upperLimit } = this.props;

        if (!startDate && !value && !this.state.startDateError)  {
            // Don't fire onChange is there's no change
            return;
        }

        // If empty, reset validation, clear date
        if (typeof value !== 'string' || value === '') {
            this.setState({
                startDateError: ''
            });
            onDatesChange({
                startDate: null,
                endDate
            });
            return;
        }

        const parsed = DateValidator({
            input: value,
            lowerLimit: lowerLimit || moment(),
            upperLimit: upperLimit || moment().add(10, 'years')
        });

        if (parsed.isValid && moment(startDate).isSame(parsed.moment, 'day')) {
            // Day hasn't changed so ignore - likely user using the calendar picker
            return;
        }

        if (!parsed.isValid) {
            this.setState({
                startDateError: parsed.errorMessage
            });

            onDatesChange({
                startDate: null,
                endDate
            });

            if (parsed.isValidMoment) {
                this.setState({
                    startDisplayErrorDate: this.getDateString(parsed.moment)
                });
            }
        }
        else if (isOutsideRange(parsed.moment) || isDayBlocked(parsed.moment)) {
            this.setState({
                startDateError: 'is not available.'
            });

            onDatesChange({
                startDate: null,
                endDate
            });

            if (parsed.isValidMoment) {
                this.setState({
                    startDisplayErrorDate: this.getDateString(parsed.moment)
                });
            }
        }
        else {
            const newStartDate = parsed.moment.startOf('day');
            // If the new start date makes the range invalid, we don't want to
            // call onDatesChange with the endDate ... we'll set it to null
            if (endDate && this.isRangeValid(newStartDate, endDate)) {
                onDatesChange({
                    startDate: newStartDate,
                    endDate
                });
            }
            else {
                onDatesChange({
                    startDate: newStartDate,
                    endDate: null
                });
            }


            this.setState({
                startDateError: '',
                startDisplayErrorDate: null
            });
        }
    };

    onStartDateChange(startDateString) {
        const startDate = toMomentObject(startDateString, this.getDisplayFormat());

        let { endDate } = this.props;
        const { isOutsideRange, minimumNights, onDatesChange } = this.props;
        const isStartDateValid = startDate && !isOutsideRange(startDate);

        if (isStartDateValid) {
            if (startDate && isBeforeDay(endDate, startDate.clone().add(minimumNights, 'days'))) {
                endDate = null;
            }

            onDatesChange({ startDate, endDate });
            // onFocusChange(END_DATE);
        }
        else {
            onDatesChange({
                startDate: null,
                endDate
            });
        }
    }

    onStartDateFocus() {
        if (!this.props.disabled) {
            this.props.onFocusChange(START_DATE);
        }
    }

    getDisplayFormat() {
        const { displayFormat } = this.props;
        return typeof displayFormat === 'string' ? displayFormat : displayFormat();
    }

    getDateString(date) {
        const displayFormat = this.getDisplayFormat();
        if (date && displayFormat) {
            return date && date.format(displayFormat);
        }
        return toLocalizedDateString(date);
    }

    isRangeValid(startDate, endDate) {
        const { minimumNights } = this.props;
        // false if range is not complete
        if (!startDate || ! endDate) {
            return false;
        }
        // false if end is before start or endDate is before startDate + minimumNights
        const minimumEndDate = startDate.clone().add(minimumNights, 'days');
        if (endDate.isBefore(minimumEndDate)) {
            return false;
        }
        return true;
    }

    clearDates() {
        const { onDatesChange, reopenPickerOnClearDates, onFocusChange } = this.props;
        this.setState(
            {
                startDateError: '',
                endDateError: '',
                startDisplayErrorDate: '',
                endDisplayErrorDate: ''
            },
            () => {
                onDatesChange({ startDate: null, endDate: null });
                if (reopenPickerOnClearDates) {
                    onFocusChange(START_DATE);
                }
            }
        );
    }

    render() {
        const {
            startDate,
            startDateId,
            startDatePlaceholderText,
            isStartDateFocused,
            endDate,
            endDateId,
            endDatePlaceholderText,
            isEndDateFocused,
            showClearDates,
            showCaret,
            showDefaultInputIcon,
            inputIconPosition,
            customInputIcon,
            customArrowIcon,
            customCloseIcon,
            disabled,
            required,
            readOnly,
            openDirection,
            isFocused,
            phrases,
            onArrowDown,
            onQuestionMark,
            isRTL
        } = this.props;

        const { startDateError, endDateError } = this.state;

        const startDateString = this.state.startDisplayErrorDate || this.getDateString(startDate);
        const endDateString = this.state.endDisplayErrorDate || this.getDateString(endDate);

        return (
            <DateRangePickerInput
                startDate={startDateString}
                startDateId={startDateId}
                startDatePlaceholderText={startDatePlaceholderText}
                startDateError={startDateError}
                isStartDateFocused={isStartDateFocused}
                endDate={endDateString}
                endDateId={endDateId}
                endDatePlaceholderText={endDatePlaceholderText}
                endDateError={endDateError}
                isEndDateFocused={isEndDateFocused}
                isFocused={isFocused}
                disabled={disabled}
                required={required}
                readOnly={readOnly}
                openDirection={openDirection}
                showCaret={showCaret}
                showDefaultInputIcon={showDefaultInputIcon}
                inputIconPosition={inputIconPosition}
                customInputIcon={customInputIcon}
                customArrowIcon={customArrowIcon}
                customCloseIcon={customCloseIcon}
                phrases={phrases}
                onStartDateBlur={this.onStartDateBlur}
                onStartDateFocus={this.onStartDateFocus}
                onStartDateShiftTab={this.onClearFocusStart}
                onEndDateBlur={this.onEndDateBlur}
                onEndDateFocus={this.onEndDateFocus}
                onEndDateTab={this.onClearFocusEnd}
                showClearDates={showClearDates}
                onClearDates={this.clearDates}
                onArrowDown={onArrowDown}
                onQuestionMark={onQuestionMark}
                isRTL={isRTL} />
        );
    }
}

DateRangePickerInputController.propTypes = propTypes;
DateRangePickerInputController.defaultProps = defaultProps;
